import { useMemo } from 'react'

import { props } from 'ramda'

import { normalize } from 'normalizr'

import { useApiQuery } from '~/src/IO/Api'
import createLogger from '~/src/Lib/Logging'
import { EMPTY_OBJECT } from '~/src/Lib/Utils'
import { capitalizeInitial } from '~/src/Lib/Utils/strings'

import { useConnectModern } from './useConnectModern'
import { SCHEMA_LOOKUP } from './utils'

const logger = createLogger('useEntityQuery')

const guard = ({
  apiOptions = EMPTY_OBJECT,
  entityKey,
  postNoWrite = false,
}) => {
  if (!entityKey) throw new TypeError('useEntityQuery must be provided an entity key')
  if (!Object.hasOwn(SCHEMA_LOOKUP, entityKey)) {
    throw new Error(`unable to find normalizr entity schema for entityKey: '${entityKey}'`)
  }
  if (apiOptions?.method && apiOptions.method !== 'GET') {
    if (apiOptions.method === 'POST' && !postNoWrite) {
      throw new TypeError('useEntityQuery only allows use of POST when postNoWrite option is truthy')
    }
    if (apiOptions.method !== 'POST') {
      throw new TypeError(`useApiQuery does not allow use of HTTP verb ${apiOptions.method}`)
    }
  }
}

const queryOptionsFnKeys = ['enabled', 'staleTime', 'refetchInterval', 'refetchOnMount', 'refetchOnWindowFocus', 'refetchOnReconnec']

/**
 * React hook to handle fetching data that should be added to the entity store.
 * Default endpoint path is /${entityKey}/ or /${entityKey}/${id}/.
 * @param {object} kwargs - Object of configuration options
 * @param {object} [kwargs.apiOptions] - options to pass to apiFetch
 * @param {object} [kwargs.entitiesReceivedMeta] - meta options to pass to doEntitiesReceived
 * @param {string} kwargs.entityKey - the key in the entity store for this query's data and default API path
 * @param {string|number} [kwargs.id] - id of the single entity
 * @param {function} [kwargs.normalizationTransform] - use this if the endpoint response can't be normalized
 * @param {object} [kwargs.params] - params to form query string (GET) or request body (POST)
 * @param {boolean} [kwargs.postNoWrite] - POST must explicitly declare itself not writing data
 * @param {object} [kwargs.queryOptions] - options for useApiQuery (will not override path, params, or queryFn)
 * @param {boolean} [kwargs.returnRaw] - should the raw API response be returned?
 * @param {string} [kwargs.url] - override the auto-generated path
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
export const useEntityQuery = kwargs => {
  guard(kwargs)
  const {
    apiOptions,
    entitiesReceivedMeta,
    entityKey,
    id,
    normalizationTransform,
    params,
    queryKeyOverride,
    queryOptions: queryOptionsParam,
    returnRaw = false,
    url,
  } = kwargs
  const { [entityKey]: entitySlice } = useConnectModern(`select${capitalizeInitial(entityKey)}`)
  const { [entityKey]: schema } = SCHEMA_LOOKUP
  const queryOptions = useMemo(() => {
    if (!queryOptionsParam) return undefined
    if (returnRaw
      || (queryOptionsFnKeys.every(key => (
        !Object.hasOwn(queryOptionsParam, key)
          || typeof queryOptionsParam[key] !== 'function'
      )))
    ) return queryOptionsParam
    return queryOptionsFnKeys.reduce((options, key) => {
      if (!(key in options) || typeof options[key] !== 'function') {
        return options
      }
      const { [key]: optionFn } = options
      options[key] = query => {
        const { state, ...rest } = query
        if (!state.data) return query
        return optionFn({
          ...rest,
          state: {
            ...state,
            data: Array.isArray(state.data)
              ? state.data.map(entityId => entitySlice[entityId]).filter(Boolean)
              : entitySlice[state.data]
          }
        })
      }

      return options
    }, { ...queryOptionsParam })
  }, [queryOptionsParam, returnRaw, entitySlice])
  const queryResult = useApiQuery({
    path: id ? `/${entityKey}/${id}/` : `/${entityKey}/`,
    params,
    queryFn: async ({ apiFetch, path, store }) => {
      const response = await apiFetch(url || path, params, apiOptions)
      logger.debug('response for', url ? { path: url, params } : { path, params }, 'is', response)
      let toNormalize = response
      if (normalizationTransform) {
        toNormalize = normalizationTransform(response)
      } else if (Array.isArray(response?.results ?? response)) {
        toNormalize = response.results ?? response
      }
      const isArray = Array.isArray(toNormalize)
      const normalized = normalize(toNormalize, isArray ? [schema] : schema)
      logger.debug('normalized to', normalized, 'with', schema)
      const { entities, result } = normalized
      store.doEntitiesReceived(entities, entitiesReceivedMeta)
      return returnRaw ? response : result
    },
    queryKeyOverride,
    queryOptions,
  })

  return useMemo(() => {
    if (returnRaw || queryResult.data === undefined) {
      return queryResult
    }

    const { data, ...rest } = queryResult
    if (Array.isArray(data)) {
      const dataArray = props(data, entitySlice).filter(Boolean)
      logger.debug('entity data for result:', data, 'in', entitySlice, 'is =>', dataArray)
      return {
        ...rest,
        data: dataArray
      }
    }

    return {
      ...rest,
      data: entitySlice[data]
    }
  }, [entitySlice, queryResult, returnRaw])
}
